import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { emailRegex, FormState, passwordRegex } from '@opu/utils';
import { useHistory, useLocation } from 'react-router-dom';
import AxiosInstance from '../../../helpers/AxiosInstance';
import { auth } from '../../../firebase-init';
import { useTranslation } from 'react-i18next';

const useForm = () => {
  const { t, i18n } = useTranslation();
  let history = useHistory();
  let location = useLocation();
  const [showResendPasswordResetBtn, setShowResendPasswordResetBtn] = useState(false);

  const [showResetPassword, setShowResetPassword] = useState(true);
  const [redirectToHost, setRedirectToHost] = useState(window.location.origin);
  const [firebaseVerificationObj, setFirebaseVerificationObj] = useState({});
  const [resetToken, setResetToken] = useState('');

  useEffect(() => {
    const queries = new URLSearchParams(location.search);

    const token = queries.get('token');
    const email = queries.get('email')?.replace(' ', '+');

    setResetToken(token || '');
    setFirebaseVerificationObj({
      oobCode: queries.get('oobCode'),
      apiKey: queries.get('apiKey'),
      email,
      generatePassword: queries.get('generatePassword'),
    });

    console.log('');
    if (token) {
      setShowResetPassword(true);
    } else if (queries.get('generatePassword') === 'true') {
      generatePassword(queries.get('oobCode'), queries.get('apiKey'), email);
      setShowResetPassword(false);
    } else if (queries.get('mode') === 'resetPassword') {
      setShowResetPassword(true);
    } else {
      setShowResetPassword(false);
      setLoading(true);
    }

    if (queries.get('isAdmin') === 'true') {
      setRedirectToHost(`${window.location.protocol}//admin.${window.location.hostname}`);
    }
  }, []);

  useEffect(() => {
    if (firebaseVerificationObj.oobCode && !showResetPassword && !firebaseVerificationObj.generatePassword) verifyAccount();
  }, [firebaseVerificationObj]);

  const [loading, setLoading] = useState(false);

  const resetPassword = () => {
    setLoading(true);

    if (resetToken) {
      AxiosInstance.post(`/auth/reset-password`, {
        token: resetToken,
        password: formState.inputValues.password,
        password_confirm: formState.inputValues.password_confirm,
      })
        .then(async () => {
          await Swal.fire(t('reset-password.successful'), '', 'success').then(() => {
            window.location.href = redirectToHost;
          });
        })
        .catch(() => {
          setShowResendPasswordResetBtn(true);
          Swal.fire(t('ERROR_INVALID_CODE'), '', 'error');
        })
        .finally(() => {
          setLoading(false);
        });
      return;
    }

    auth
      .confirmPasswordReset(firebaseVerificationObj.oobCode, formState.inputValues.password)
      .then(async () => {
        await Swal.fire(t('reset-password.successful'), '', 'success').then(() => {
          window.location.href = redirectToHost;
        });
      })
      .catch(() => {
        setShowResendPasswordResetBtn(true);
        Swal.fire(t('ERROR_INVALID_CODE'), '', 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const verifyAccount = () => {
    AxiosInstance.post(`https://identitytoolkit.googleapis.com/v1/accounts:update?key=${firebaseVerificationObj.apiKey}`, {
      oobCode: firebaseVerificationObj.oobCode,
    })

      .then(async (response) => {
        await Swal.fire(t('email-verification.successful'), '', 'success').then(() => {
          window.location.href = redirectToHost;
        });
      })
      .catch((error) => {
        setShowResendPasswordResetBtn(true);
        Swal.fire(t('email-verification.error'), '', 'error').then(() => {
          window.location.href = redirectToHost;
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const inputValues = {
    password: { value: '', validate: { required: true, min: 8, pattern: passwordRegex } },
    password_confirm: { value: '', validate: { required: true } },
  };
  const { formState, submitHandler, updateFormState, field, handleErrorsServer } = FormState(inputValues, resetPassword);

  const sendPasswordResetEmail = () => {
    if (auth.currentUser) {
      auth
        .sendPasswordResetEmail(auth.currentUser.email)
        .then(() => {
          Swal.fire(t('profile.password-reset-email-sent'), '', 'success');
        })
        .catch(() => {
          Swal.fire(t('profile.error-send-password-reset-email'), '', 'error');
        });
      return;
    }

    if (firebaseVerificationObj.email) {
      sendPasswordResetEmailThroughApi(firebaseVerificationObj.email);
      return;
    }

    history.push('password-recovery');
  };

  const sendPasswordResetEmailThroughApi = (email) => {
    setLoading(true);
    AxiosInstance.post(`/auth/send-password-recovery-email`, { email })
      .then((res) => {
        Swal.fire(t('PASSWORD_RESET_SUCCESS'), '', 'success').then(() => {
          window.location.href = window.location.origin;
        });
      })
      .catch((error) => {
        Swal.fire(t('PASSWORD_RESET_FAILED'), '', 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const generatePassword = async (oobCode, apiKey, email) => {
    setLoading(true);

    AxiosInstance.post(`/auth/generate_password_link`, { oobCode, apiKey, email })
      .then((res) => {
        console.log('inside');
        window.location.href = res.password_recovery_link;
      })
      .catch((error) => {
        console.log('error');

        Swal.fire({
          title: t('SESSION_EXPIRED'),
          showConfirmButton: true,
          showDenyButton: true,
          confirmButtonText: t('RESEND_PASSWORD_RECOVERY_LINK'),
          denyButtonText: t('annuler-modal-inactivated'),
          allowOutsideClick: false,
        }).then((result) => {
          if (result.isConfirmed) {
            sendPasswordResetEmailThroughApi(email);
          } else {
            window.location.href = window.location.origin;
          }
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return {
    submitHandler,
    loading,
    field,
    sendPasswordResetEmail,
    showResendPasswordResetBtn,
    showResetPassword,
  };
};

export default useForm;
